import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export function AdminContactPanel() {
  const { t } = useTranslation();
  const [info, setInfo] = useState({ email: "", phone: "", address: "", hours: "", whatsapp: "", extras: [] });

  useEffect(() => { api.get("/contact-info").then((r) => setInfo({ extras: [], ...r.data })).catch(() => {}); }, []);

  const save = async () => {
    try {
      const payload = { ...info, extras: (info.extras || []).filter((e) => e.label || e.value) };
      await api.put("/admin/contact-info", payload);
      toast.success(t("contactInfo.saved"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const setField = (k, v) => setInfo((p) => ({ ...p, [k]: v }));
  const setExtra = (i, k, v) => setInfo((p) => ({ ...p, extras: p.extras.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const addExtra = () => setInfo((p) => ({ ...p, extras: [...(p.extras || []), { label: "", value: "" }] }));
  const removeExtra = (i) => setInfo((p) => ({ ...p, extras: p.extras.filter((_, j) => j !== i) }));

  const FIELDS = [
    { k: "email", label: t("contactInfo.email") },
    { k: "phone", label: t("contactInfo.phone") },
    { k: "whatsapp", label: t("contactInfo.whatsapp") },
    { k: "address", label: t("contactInfo.address") },
    { k: "hours", label: t("contactInfo.hours") },
  ];

  return (
    <div data-testid="admin-contact-panel" className="bg-white rounded-2xl shadow-card border border-border p-6 max-w-2xl">
      <h3 className="font-heading font-bold text-xl text-ink">{t("contactInfo.title")}</h3>
      <p className="text-sm text-muted2 font-body mt-1">{t("contactInfo.subtitle")}</p>
      <div className="space-y-4 mt-6">
        {FIELDS.map((f) => (
          <div key={f.k}>
            <label className="text-xs font-bold uppercase text-muted2">{f.label}</label>
            <Input data-testid={`contact-field-${f.k}`} value={info[f.k] || ""} onChange={(e) => setField(f.k, e.target.value)} className="mt-1 h-11 rounded-xl bg-sand" />
          </div>
        ))}

        <div className="pt-2 border-t border-border">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase text-muted2">{t("contactInfo.extras")}</label>
            <Button size="sm" variant="outline" data-testid="contact-add-field" onClick={addExtra} className="rounded-full h-8"><Plus className="w-3.5 h-3.5 mr-1" /> {t("contactInfo.addField")}</Button>
          </div>
          <div className="space-y-2 mt-2">
            {(info.extras || []).map((ex, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input data-testid={`contact-extra-label-${i}`} placeholder={t("contactInfo.fieldLabel")} value={ex.label} onChange={(e) => setExtra(i, "label", e.target.value)} className="h-10 rounded-xl bg-sand" />
                <Input data-testid={`contact-extra-value-${i}`} placeholder={t("contactInfo.fieldValue")} value={ex.value} onChange={(e) => setExtra(i, "value", e.target.value)} className="h-10 rounded-xl bg-sand" />
                <Button size="icon" variant="ghost" data-testid={`contact-remove-field-${i}`} onClick={() => removeExtra(i)} className="shrink-0 text-destructive"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </div>
        </div>

        <Button data-testid="contact-info-save" onClick={save} className="w-full h-11 rounded-full bg-emeraude hover:bg-emeraude-dark font-bold"><Save className="w-4 h-4 mr-2" /> {t("contactInfo.save")}</Button>
      </div>
    </div>
  );
}
